import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Forms from "../../store/ducks/forms.duck";
import * as Auth from "../../store/ducks/auth.duck";
import FormDataEntry from "../../pages/home/admin/FormDataEntry";

import { Card, CardContent, Typography, Button, Box, Chip, Divider, Modal, IconButton } from "@material-ui/core";

import AssignmentIcon from "@material-ui/icons/Assignment";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";

import { makeStyles } from "@material-ui/core/styles";
import { ToastContainer, Flip } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 1000,
    margin: "0 auto",
  },

  intro: {
    marginBottom: theme.spacing(3),
  },

  introTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },

  introText: {
    color: "#6c757d",
  },

  card: {
    border: "1px solid #e4e6ef",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  },

  cardHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 2.5),
  },

  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 7,
    background: "#eef1ff",
    color: "#5667c9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1.5),
  },

  headerTitle: {
    fontWeight: 500,
  },

  formRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),

    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
    },
  },

  formDetails: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },

  formIcon: {
    color: "#7e8299",
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },

  formName: {
    fontWeight: 500,
    color: "#3f4254",
  },

  status: {
    marginTop: 4,
    fontSize: 11,
  },

  action: {
    marginLeft: theme.spacing(2),
    flexShrink: 0,
  },

  empty: {
    textAlign: "center",
    padding: theme.spacing(5, 3),
  },

  completedIcon: {
    fontSize: 45,
    color: "#1bc5bd",
    marginBottom: theme.spacing(1),
  },

  modal: {
    background: "#fff",
    width: "90%",
    maxWidth: 900,
    maxHeight: "90vh",
    overflow: "auto",
    margin: "5vh auto",
    borderRadius: 8,
  },

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
  },
}));

export default function DashboardCSClient() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const ssoFormDisplay = useSelector((state) => state.auth.SSOForm);
  const allFormData = useSelector((state) => state.forms.AllFormData || []);

  const formDataToDo = allFormData.filter((row) => row.formDataStatus === "Not Started" || row.formDataStatus === "In Progress");

  const [showFormData, setShowFormData] = useState(false);
  const [formData, setFormData] = useState();

  useEffect(() => {
    dispatch(Forms.actions.fetchAllFormData(0));
  }, [dispatch]);

  const loadFormDataEntry = (form) => {
    setFormData(form);
    setShowFormData(true);
  };

  useEffect(() => {
    if (ssoFormDisplay && allFormData && allFormData.length > 0) {
      const selectedForm = allFormData.find((form) => form.formDataID === Number(ssoFormDisplay));

      if (selectedForm) {
        loadFormDataEntry(selectedForm);
      }

      dispatch(Auth.actions.ssoformclear());
    }
  }, [ssoFormDisplay, allFormData, dispatch]);

  return (
    <>
      <div className={classes.container}>
        <div className={classes.intro}>
          <Typography variant="h5" className={classes.introTitle}>
            Forms requiring your attention
          </Typography>

          <Typography variant="body2" className={classes.introText}>
            Please complete the forms below
          </Typography>
        </div>

        <Card className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.headerIcon}>
              <AssignmentIcon />
            </div>

            <Typography variant="h6" className={classes.headerTitle}>
              Outstanding forms
            </Typography>
          </div>

          <Divider />

          {formDataToDo.length > 0 ? (
            formDataToDo.map((form, index) => (
              <React.Fragment key={`form-${form.formDataID}`}>
                <div className={classes.formRow}>
                  <div className={classes.formDetails}>
                    <AssignmentIcon className={classes.formIcon} />

                    <div>
                      <Typography className={classes.formName}>{form.formName}</Typography>

                      <Chip label={form.formDataStatus} size="small" variant="outlined" className={classes.status} />
                    </div>
                  </div>

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.action}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => loadFormDataEntry(form)}
                  >
                    {form.formDataStatus === "In Progress" ? "Continue" : "Start form"}
                  </Button>
                </div>

                {index < formDataToDo.length - 1 && <Divider />}
              </React.Fragment>
            ))
          ) : (
            <div className={classes.empty}>
              <CheckCircleIcon className={classes.completedIcon} />

              <Typography variant="h6">All forms completed</Typography>

              <Typography variant="body2" color="textSecondary">
                You don't currently have any outstanding forms.
              </Typography>
            </div>
          )}
        </Card>
      </div>

      <ToastContainer
        position="bottom-right"
        transition={Flip}
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Modal open={showFormData} onClose={() => setShowFormData(false)}>
        <div className={classes.modal}>
          <div className={classes.modalHeader}>
            <Typography variant="h6">{formData && formData.formName}</Typography>

            <IconButton size="small" onClick={() => setShowFormData(false)}>
              <CloseIcon />
            </IconButton>
          </div>

          <Divider />

          <Box p={3}>{formData && <FormDataEntry formID={formData.formID} formDataID={formData.formDataID} setShowFormData={setShowFormData} />}</Box>
        </div>
      </Modal>
    </>
  );
}
